import React, { useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, SafeAreaView, StatusBar, Alert, TextInput } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

const API = 'https://api.jobeng.ai';
const KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const P = '#0e4e96';
const getH = async () => { const t = await AsyncStorage.getItem('auth_token').catch(() => null); return { 'Content-Type': 'application/json', 'x-api-key': KEY, ...(t ? { Authorization: `Bearer ${t}` } : {}) }; };

export default function TaskDetailsScreen({ navigation, route }) {
  const { task: initial = {}, employeeId, onUpdate } = route?.params || {};
  const [task, setTask] = useState(initial);
  const [progress, setProgress] = useState(String(initial.progress || 0));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const p = Math.min(100, Math.max(0, parseInt(progress) || 0));
    setSaving(true);
    try {
      const h = await getH();
      const res = await fetch(`${API}/api/goals/${task.id}/progress`, {
        method: 'PUT', headers: h, body: JSON.stringify({ progress: p }),
      });
      const json = await res.json().catch(() => ({}));
      if (json.success || res.ok) {
        setTask(t => ({ ...t, progress: p }));
        if (typeof onUpdate === 'function') onUpdate();
        Alert.alert('✅ Updated', `Progress set to ${p}%`);
      } else Alert.alert('Error', json.error || 'Could not update');
    } catch (e) { Alert.alert('Error', e.message); }
    finally { setSaving(false); }
  };

  const p = parseInt(task.progress) || 0;
  const dueDate = task.due_date ? new Date(task.due_date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : null;
  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';

  return (
    <SafeAreaView style={s.safe}>
      <StatusBar barStyle="light-content" backgroundColor={P} />
      <LinearGradient colors={[P, '#1e7be2']} style={s.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={s.back}>
          <MaterialCommunityIcons name="arrow-left" size={22} color="#fff" />
        </TouchableOpacity>
        <Text style={s.headerTitle}>Goal Details</Text>
        <View style={{ width: 36 }} />
      </LinearGradient>
      <ScrollView style={s.scroll}>
        {/* Hero card */}
        <View style={s.heroCard}>
          <View style={s.heroTop}>
            <View style={[s.priorityBadge, { backgroundColor: task.priority === 'high' ? '#fee2e2' : task.priority === 'medium' ? '#fef3c7' : '#dcfce7' }]}>
              <Text style={[s.priorityText, { color: task.priority === 'high' ? '#ef4444' : task.priority === 'medium' ? '#f59e0b' : '#10b981' }]}>
                {(task.priority || 'medium').toUpperCase()} PRIORITY
              </Text>
            </View>
            <View style={[s.statusBadge, { backgroundColor: task.status === 'completed' ? '#dcfce7' : '#e0e7ff' }]}>
              <Text style={[s.statusText, { color: task.status === 'completed' ? '#10b981' : P }]}>
                {(task.status || 'active').toUpperCase()}
              </Text>
            </View>
          </View>
          <Text style={s.title}>{task.title}</Text>
          {task.description ? <Text style={s.desc}>{task.description}</Text> : null}
        </View>

        {/* Progress */}
        <View style={s.card}>
          <Text style={s.cardTitle}>Progress</Text>
          <View style={s.progressBig}>
            <View style={s.progressTrack}>
              <View style={[s.progressFill, { width: `${p}%` }]} />
            </View>
            <Text style={s.progressPct}>{p}%</Text>
          </View>
          <View style={s.updateRow}>
            <TextInput
              style={s.progressInput}
              value={progress}
              onChangeText={setProgress}
              keyboardType="numeric"
              placeholder="0-100"
              maxLength={3}
            />
            <Text style={s.pctLabel}>%</Text>
            <TouchableOpacity style={[s.updateBtn, saving && { opacity: 0.5 }]} onPress={save} disabled={saving}>
              <Text style={s.updateBtnText}>{saving ? 'Saving...' : 'Update Progress'}</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Details */}
        <View style={s.card}>
          <Text style={s.cardTitle}>Details</Text>
          {[
            ['Category', task.category || 'General'],
            ['Target', task.target || 'Not specified'],
            ['Due Date', dueDate || 'No deadline'],
            ['Created', task.created_at ? new Date(task.created_at).toLocaleDateString('en-GB') : '—'],
          ].map(([label, value]) => (
            <View key={label} style={s.detailRow}>
              <Text style={s.detailLabel}>{label}</Text>
              <Text style={[s.detailValue, label === 'Due Date' && isOverdue && { color: '#ef4444', fontWeight: '700' }]}>{value}</Text>
            </View>
          ))}
          {isOverdue && <Text style={s.overdueWarn}>⚠️ This goal is past its due date</Text>}
        </View>

        {/* Quick progress buttons */}
        <View style={s.card}>
          <Text style={s.cardTitle}>Quick Update</Text>
          <View style={s.quickRow}>
            {[25, 50, 75, 100].map(pct => (
              <TouchableOpacity key={pct} style={[s.quickBtn, p === pct && s.quickBtnActive]}
                onPress={() => { setProgress(String(pct)); }}>
                <Text style={[s.quickBtnText, p === pct && s.quickBtnTextActive]}>{pct}%</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const s = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#f8fafc' },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14 },
  back: { padding: 4 },
  headerTitle: { color: '#fff', fontSize: 18, fontWeight: '700' },
  scroll: { flex: 1 },
  heroCard: { margin: 16, backgroundColor: '#fff', borderRadius: 20, padding: 20, shadowColor: '#000', shadowOpacity: 0.08, shadowRadius: 12, elevation: 4 },
  heroTop: { flexDirection: 'row', gap: 8, marginBottom: 12 },
  priorityBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 8 },
  priorityText: { fontSize: 10, fontWeight: '800' },
  statusBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 8 },
  statusText: { fontSize: 10, fontWeight: '800' },
  title: { fontSize: 20, fontWeight: '800', color: '#1e293b', marginBottom: 8, lineHeight: 28 },
  desc: { fontSize: 14, color: '#64748b', lineHeight: 22 },
  card: { marginHorizontal: 16, marginBottom: 12, backgroundColor: '#fff', borderRadius: 16, padding: 16, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 6, elevation: 2 },
  cardTitle: { fontSize: 13, fontWeight: '800', color: '#94a3b8', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 },
  progressBig: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 16 },
  progressTrack: { flex: 1, height: 12, backgroundColor: '#e2e8f0', borderRadius: 6 },
  progressFill: { height: 12, borderRadius: 6, backgroundColor: P },
  progressPct: { fontSize: 24, fontWeight: '900', color: P, width: 60 },
  updateRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  progressInput: { width: 60, backgroundColor: '#f8fafc', borderRadius: 10, borderWidth: 1, borderColor: '#e2e8f0', padding: 10, fontSize: 18, fontWeight: '700', textAlign: 'center', color: '#1e293b' },
  pctLabel: { fontSize: 18, fontWeight: '700', color: '#64748b' },
  updateBtn: { flex: 1, backgroundColor: P, borderRadius: 12, padding: 12, alignItems: 'center' },
  updateBtnText: { color: '#fff', fontWeight: '700', fontSize: 14 },
  detailRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' },
  detailLabel: { fontSize: 13, color: '#64748b', fontWeight: '600' },
  detailValue: { fontSize: 13, color: '#1e293b', fontWeight: '700', textAlign: 'right', flex: 1, marginLeft: 16 },
  overdueWarn: { fontSize: 12, color: '#ef4444', marginTop: 10, fontWeight: '600' },
  quickRow: { flexDirection: 'row', gap: 8 },
  quickBtn: { flex: 1, backgroundColor: '#f8fafc', borderRadius: 10, padding: 12, alignItems: 'center', borderWidth: 1.5, borderColor: '#e2e8f0' },
  quickBtnActive: { backgroundColor: P, borderColor: P },
  quickBtnText: { fontSize: 14, fontWeight: '700', color: '#64748b' },
  quickBtnTextActive: { color: '#fff' },
});
